import React, { useMemo, useState, useEffect, useCallback, useRef, useId } from 'react';
import { useWindowSize } from 'usehooks-ts';

const ResponsiveImageSet = (props) => {
    const hasBreakpoints = props.breakpoints && props.breakpoints.length > 0;
    // useId produces a stable, SSR-safe, deterministic ID — no Math.random() needed
    const reactId = useId();
    const uniqueId = 'img-' + reactId.replace(/:/g, '');
    const imgRef = useRef(null);

    const { width: windowWidth } = useWindowSize();
    const [currentScale, setCurrentScale] = useState(1);
    const [naturalAspectRatio, setNaturalAspectRatio] = useState(null);

    const updateScale = useCallback(() => {
        if (!props.breakpoints || props.breakpoints.length === 0) return;
        const sorted = [...props.breakpoints].sort((a, b) => b.minWidth - a.minWidth);
        const activeBp = sorted.find((bp) => windowWidth >= bp.minWidth);
        const scale = activeBp && activeBp.scale !== undefined ? activeBp.scale : 1;
        setCurrentScale(scale);
    }, [props.breakpoints, windowWidth]);

    useEffect(() => {
        updateScale();
    }, [updateScale]);

    // Handle initial load / cached image on mount
    useEffect(() => {
        if (imgRef.current && imgRef.current.complete) {
            const width = imgRef.current.naturalWidth;
            const height = imgRef.current.naturalHeight;
            if (width && height) {
                setNaturalAspectRatio(width / height);
            }
        }
    }, []);

    const handleLoad = (e) => {
        const width = e.target.naturalWidth;
        const height = e.target.naturalHeight;
        if (width && height) {
            setNaturalAspectRatio(width / height);
        }
    };

    const imgStyle = useMemo(() => {
        if (!naturalAspectRatio || currentScale === 1) return {};
        return {
            aspectRatio: `${naturalAspectRatio / currentScale}`,
            objectFit: 'cover',
        };
    }, [naturalAspectRatio, currentScale]);

    const stylesheetContent = useMemo(() => {
        if (!hasBreakpoints) return '';
        return [...props.breakpoints]
            .sort((a, b) => a.minWidth - b.minWidth)
            .map((bp) => {
                const posX = bp.focalPoint?.x !== undefined && bp.focalPoint?.x !== null ? bp.focalPoint.x : 50;
                const posY = bp.focalPoint?.y !== undefined && bp.focalPoint?.y !== null ? bp.focalPoint.y : 50;
                const maxHeightStyle =
                    bp.maxHeight !== undefined && bp.maxHeight !== null
                        ? `max-height: ${bp.maxHeight}px !important; object-fit: cover !important;`
                        : '';
                return `@media (min-width: ${bp.minWidth}px) {
                    .${uniqueId} {
                        object-position: ${posX}% ${posY}% !important;
                        ${maxHeightStyle}
                    }
                }`;
            })
            .join('\n');
    }, [hasBreakpoints, props.breakpoints, uniqueId]);

    if (hasBreakpoints) {
        // Sort breakpoints by minWidth in descending order (largest first)
        // so browser evaluates the media queries correctly inside <picture>
        const sortedBreakpoints = [...props.breakpoints].sort((a, b) => b.minWidth - a.minWidth);

        // Fallback image source should be the smallest minWidth breakpoint,
        // or the props.imageLg fallback
        const smallestBreakpoint = sortedBreakpoints[sortedBreakpoints.length - 1];
        const defaultImgSrc = smallestBreakpoint ? smallestBreakpoint.url : props.imageLg;

        return (
            <div className="w-100" style={props.style}>
                <style dangerouslySetInnerHTML={{ __html: stylesheetContent }} />
                <picture className="w-100">
                    {sortedBreakpoints.map((bp, idx) => (
                        <source key={idx} media={`(min-width: ${bp.minWidth}px)`} srcSet={bp.url} />
                    ))}
                    <img
                        ref={imgRef}
                        src={defaultImgSrc}
                        className={`w-100 img-fluid ${uniqueId}`}
                        alt={props.altText !== undefined && props.altText !== null ? props.altText : ''}
                        onLoad={handleLoad}
                        style={imgStyle}
                    />
                </picture>
            </div>
        );
    }

    return (
        <div className="w-100" style={props.style}>
            <picture className="w-100">
                <source media="(max-width: 767px)" srcSet={props.imageXs} />

                <source media="(min-width: 768px)" srcSet={props.imageLg} />

                <img
                    src={props.imageLg}
                    className="w-100 img-fluid"
                    alt={props.altText !== undefined && props.altText !== null ? props.altText : ''}
                />
            </picture>
        </div>
    );
};

export default ResponsiveImageSet;
